/**
 * Reporting something broken: describe it, then answer 0-3 quick questions.
 *
 * Both incident front doors — `/tickets/new` and the assistant's inline card —
 * render this one component, so they ask the same (few) things and file the
 * same shape of incident. It owns the form and the "Continue" round trip; the
 * owner decides what filing means (navigate to the ticket, or turn the card
 * into a receipt) through `onSubmit`.
 *
 * It replaced a category picklist plus up to five narrative questions, which
 * people found far too much. What is asked now:
 *
 *   1. **What's happening?** — one box, their own words, screenshots pasted
 *      straight in. The only required answer.
 *   2. **How much is this affecting your work?** — three plain choices that map
 *      onto urgency. Impact stays IT's to set: nobody reporting a broken laptop
 *      can know how many people it affects.
 *   3. An optional subject. No location or phone: the person is signed in,
 *      and Ivanti already has both on their employee record.
 *
 * **Continue** sends the description (and any screenshots) to
 * `getIncidentFollowups`, which classifies it silently, writes a subject if
 * there is none, and returns zero to three questions a technician would
 * genuinely need answered. When there are questions, one more always closes
 * the step — "Anything else?", a large box with its own attach control, for
 * the longer explanation and screenshots that help IT most. Its text travels as
 * `additional_details`; its attachments join the same list as the first
 * uploader's. Zero questions files straight away (unless a
 * screenshot is still uploading — then it stops at "Ready to submit"). A
 * failure files straight away too: the follow-ups make a ticket better, and
 * must never be the reason one cannot be raised.
 *
 * A retry must not become a second ticket, so triage is cached against its
 * inputs and the attempt is fingerprinted on what the person entered, never on
 * the subject or category triage derived (`lib/incident-attempt.ts`).
 *
 * From the assistant's card (`fromChat`) the chat has already done the asking,
 * so the reporter opens straight on the review step: the assistant's questions
 * with the answers it heard (if it asked any), the "Anything else?" box with its
 * own attach control, and Submit. Triage then runs in the background only to
 * pick the category — it never adds questions and never files on its own
 * (`lib/incident-review.ts`).
 *
 * Anyone may report for a colleague: "Reporting this for someone else?" opens
 * the directory picker (`OnBehalfField`), and the chat card opens on "For:
 * <name>" when the assistant already found them. The body then carries
 * `on_behalf_of_email`; for the signed-in person it carries nothing extra.
 */

import {
  Button,
  cn,
  Field,
  Form,
  Input,
  Radio,
  RadioGroup,
  Separator,
  Textarea,
  Typography,
} from "@norican/react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link } from "react-router";

import { AttachmentUploader, usePasteToAttach } from "@/components/attachments";
import {
  ErrorState,
  FieldHighlightLegend,
  FormAnnouncer,
  FormReview,
  type FormReviewRow,
  HighlightedField,
  REVIEW_HIDES_FORM,
  useFormAnnouncer,
  useFormCheckMessage,
  useSubmitLabel,
} from "@/components/common";
import { FilingAsLine } from "@/components/layout";
import { useLanguage, useT } from "@/i18n";
import { useKiosk, useMe } from "@/lib/api";
import { prefillHighlight, useFieldHighlight } from "@/lib/field-highlight";
import { needsReviewStep, stillNeededCount } from "@/lib/form-checks";
import { logger } from "@/lib/logger";
import { revealFieldSoon, revealFirstProblemSoon } from "@/lib/reveal-first-problem";

import type {
  IncidentCreateRequest,
  IncidentFollowupQuestion,
  SimilarTicket,
  TicketFollowup,
  TicketLevelCode,
} from "../api/contract";
import { useFindSimilarTickets, useIncidentFollowups, useJoinSimilarTicket } from "../api/queries";
import {
  incidentAttemptFingerprint,
  type IncidentTriageInput,
  incidentTriageKey,
  ownIncidentSubject,
} from "../lib/incident-attempt";
import {
  chatFollowupQuestions,
  offersSkip,
  reporterStart,
  type ReporterStep,
  showsAnythingElse,
} from "../lib/incident-review";
import { isSomeoneElse, onBehalfField, type OnBehalfPerson } from "../lib/on-behalf";
import {
  type NotSameDecision,
  notSameNumbers,
  shouldCheckSimilar,
  withNotSameAs,
} from "../lib/similar-tickets";
import {
  ADDITIONAL_DETAILS_MAX_LENGTH,
  DESCRIPTION_MAX_LENGTH,
  FOLLOWUP_ANSWER_MAX_LENGTH,
  SUBJECT_MAX_LENGTH,
  validateIncidentDetails,
  validateOptionalSubject,
  type ValidationKey,
} from "../lib/validation";
import { OnBehalfField } from "./on-behalf-field";
import { type JoinedTicket, SimilarTicketPrompt } from "./similar-ticket-prompt";
import { useValidationMessage } from "./ticket-basics-fields";

/** The three impact answers, most severe first, and the urgency each sets. */
const IMPACT_CHOICES = ["HIGH", "MEDIUM", "LOW"] as const satisfies readonly TicketLevelCode[];

/** What the form holds before anything is typed. */
export interface IncidentReporterInitial {
  subject?: string | null;
  details?: string | null;
  urgency?: TicketLevelCode | null;
  /** Questions the assistant already asked, with the answers it heard. */
  followups?: TicketFollowup[];
  /** The colleague the assistant found, when the person said it is for them. */
  onBehalfOf?: OnBehalfPerson | null;
}

export interface IncidentReporterProps {
  /** Prefill — from the assistant, or nothing on `/tickets/new`. */
  initial?: IncidentReporterInitial;
  /**
   * Draft attachment ids that arrived from elsewhere (the chat turn), merged
   * with whatever is added here.
   */
  extraAttachmentIds?: string[];
  /** Threaded onto the ticket when it was raised from a conversation. */
  conversationId?: string | null;
  /**
   * Opened from the assistant's card: the conversation already asked the
   * follow-ups, so start on the review step and never ask triage's questions.
   */
  fromChat?: boolean;
  /** The owner's filing is in flight. */
  submitting: boolean;
  /** The owner's filing failed; shown inline. */
  submitError?: unknown;
  /**
   * File it. The body is complete and validated. `fingerprint` identifies the
   * attempt by what the person entered (not what triage derived) — key the
   * idempotency key on it. A returned promise holds off a second filing until
   * it settles. `forPerson` is the colleague it is raised for, or null when it
   * is the signed-in person's own — so the owner can say "Raised for <name>".
   */
  onSubmit: (
    body: IncidentCreateRequest,
    fingerprint: string,
    forPerson: OnBehalfPerson | null,
  ) => void | Promise<unknown>;
  /** Rendered beside the primary button when given (the page's Cancel). */
  onCancel?: () => void;
  /**
   * "Is this the same problem?" -> "Yes, add me to it" (8 Oct): the person was
   * noted on someone else's open ticket and nothing was filed. The owner
   * replaces the form with `SimilarJoinedNotice`.
   */
  onJoined: (joined: JoinedTicket) => void;
}

/** An open ticket about the same thing was found: the form waits for an answer. */
interface SimilarPending {
  matches: SimilarTicket[];
  body: IncidentCreateRequest;
  fingerprint: string;
  /** The text the check ran on; "add me" sends the same, for the server to re-check. */
  text: { subject: string | null; description: string; system: string | null };
}

type Step = ReporterStep;

/** What triage derived: used only where the person left a gap. */
interface Derived {
  subject: string | null;
  category: string | null;
  /** The Ivanti Service and Category names, both or neither. */
  service: string | null;
  serviceCategory: string | null;
}

/** One triage answer, cached against its inputs. */
interface TriageOutcome {
  found: Derived;
  asked: IncidentFollowupQuestion[];
}

const NOTHING_DERIVED: Derived = {
  subject: null,
  category: null,
  service: null,
  serviceCategory: null,
};

/** Whether two question lists ask the same things, so answers can be kept. */
function sameQuestions(
  a: readonly IncidentFollowupQuestion[],
  b: readonly IncidentFollowupQuestion[],
): boolean {
  return a.length === b.length && a.every((q, i) => q.id === b[i]?.id && q.text === b[i]?.text);
}

export function IncidentReporter({
  initial,
  extraAttachmentIds = [],
  conversationId = null,
  fromChat = false,
  submitting,
  submitError = null,
  onSubmit,
  onCancel,
  onJoined,
}: IncidentReporterProps) {
  const { t } = useT("tickets");
  const { t: tCommon } = useT();
  const { language } = useLanguage();
  const message = useValidationMessage();
  const attachmentsId = useId();
  const moreAttachmentsId = useId();
  const followups = useIncidentFollowups();
  const findSimilar = useFindSimilarTickets();
  const joinSimilar = useJoinSimilarTicket();
  /** "Is this the same problem?" is asked once per attempt (its fingerprint). */
  const similarChecked = useRef<string | null>(null);
  /** "No, mine is different", for the attempt it answered. */
  const notSame = useRef<NotSameDecision | null>(null);
  const [similar, setSimilar] = useState<SimilarPending | null>(null);
  const [joiningId, setJoiningId] = useState<string | null>(null);
  const [joinFailed, setJoinFailed] = useState(false);
  const [filingOwn, setFilingOwn] = useState(false);
  const { data: me } = useMe();
  const signedInEmail = me?.email ?? null;

  const chatQuestions = useMemo<IncidentFollowupQuestion[]>(
    () => chatFollowupQuestions(initial?.followups).questions,
    [initial?.followups],
  );

  /* Decided once, from the prefill as it arrived: a chat card opens on review
     when what the assistant filled in would pass validation. */
  const [start] = useState(() =>
    reporterStart(initial?.followups, {
      fromChat,
      prefillValid:
        validateIncidentDetails(initial?.details ?? "") === null &&
        validateOptionalSubject(initial?.subject ?? "") === null,
    }),
  );

  const [step, setStep] = useState<Step>(start.step);
  const [subject, setSubject] = useState(initial?.subject ?? "");
  const [details, setDetails] = useState(initial?.details ?? "");
  // Field-state tint (5 Oct): blue while the chat's text stands, yellow while empty.
  const subjectState = useFieldHighlight(initial?.subject ?? null, subject, false);
  const detailsState = useFieldHighlight(initial?.details ?? null, details, true);
  const [urgency, setUrgency] = useState<TicketLevelCode>(initial?.urgency ?? "MEDIUM");
  /* The rest of what the chat filled in is tinted the same way (7 Oct): the
     impact it heard, the colleague it found and the answers to its questions.
     All optional, so blue while unchanged and plain otherwise — never yellow. */
  const urgencyState = prefillHighlight(initial?.urgency ?? null, urgency);
  const [subjectError, setSubjectError] = useState<ValidationKey | null>(() =>
    start.step === "describe" && fromChat ? validateOptionalSubject(initial?.subject ?? "") : null,
  );
  const [detailsError, setDetailsError] = useState<ValidationKey | null>(() =>
    start.step === "describe" && fromChat ? validateIncidentDetails(initial?.details ?? "") : null,
  );
  const [questions, setQuestions] = useState<IncidentFollowupQuestion[]>(start.questions);
  // Seeded once from what the assistant heard.
  const [answers, setAnswers] = useState<Record<string, string>>(start.answers);
  /** The closing "Anything else?" answer. */
  const [additional, setAdditional] = useState("");
  const [triage, setTriage] = useState<Derived>(NOTHING_DERIVED);
  /* Raising for a colleague is refused on a shared device (KIOSK_NOT_ALLOWED):
     the "signed-in person" there is the device, so nobody is offered it and an
     assistant prefill naming one is dropped (1 Oct). */
  const kiosk = useKiosk();
  /** Who it is for: null is the signed-in person, which is the default. */
  const [forPerson, setForPerson] = useState<OnBehalfPerson | null>(
    kiosk === null ? (initial?.onBehalfOf ?? null) : null,
  );
  /** The colleague the chat found, as it arrived; null when it found nobody. */
  const [prefilledFor] = useState<OnBehalfPerson | null>(() => forPerson);
  const forPersonState = prefillHighlight(
    prefilledFor?.email.toLowerCase() ?? null,
    forPerson?.email.toLowerCase() ?? null,
  );
  /** A blocking triage call is in flight (not the background one for chat). */
  const [checking, setChecking] = useState(false);

  const [uploading, setUploading] = useState(false);

  /* Idiot-proof forms (7 Oct). A colleague's name typed in "Reporting this
     for someone else?" but not picked from the list would otherwise file the
     incident as the reporter's own without a word. */
  const [forPending, setForPending] = useState(false);
  const [forProblem, setForProblem] = useState(false);
  /** "Here's what IT will get" is on screen instead of the form. */
  const [reviewing, setReviewing] = useState(false);
  /** How many files each uploader holds, for the summary. */
  const [fileCounts, setFileCounts] = useState({ first: 0, later: 0 });
  const { message: announcement, announce } = useFormAnnouncer();
  const submitLabel = useSubmitLabel();
  const checkMessage = useFormCheckMessage();
  // The count on the button: "What's happening?" is the one required answer.
  const stillNeeded = stillNeededCount([details.trim() === ""]);

  /* A failed submit puts the error at the top of a long form; on a phone the
     submit button is a screen away from it, so bring it into view. */
  const formRef = useRef<HTMLFormElement>(null);
  useEffect(() => {
    if (submitError !== null && submitError !== undefined) {
      revealFirstProblemSoon(() => formRef.current);
    }
  }, [submitError]);

  /* Read at file time, after an await, so they must not come from the render
     that started the Continue. `addedIdsRef` is the first uploader's,
     `laterIdsRef` the "Anything else?" one's; both land on the same ticket. */
  const addedIdsRef = useRef<string[]>([]);
  const laterIdsRef = useRef<string[]>([]);
  const extraIdsRef = useRef<string[]>(extraAttachmentIds);
  const uploadingRef = useRef(false);
  const pendingRef = useRef({ first: false, later: false });
  useEffect(() => {
    extraIdsRef.current = extraAttachmentIds;
  }, [extraAttachmentIds]);

  /** Triage answers by input, so a retry never re-asks and gets a new subject. */
  const triageCache = useRef(new Map<string, TriageOutcome>());
  /** What the current attempt first filed with, reused by its retries. */
  const filedWith = useRef<{ fingerprint: string; found: Derived } | null>(null);
  /* Synchronous guards: state updates land a render too late for a double click. */
  const continuing = useRef(false);
  const filing = useRef(false);

  const busy = checking || submitting;
  const anythingElse = showsAnythingElse({ step, questionCount: questions.length, fromChat });
  // With follow-up answers in, the summary comes before the send (7 Oct).
  const reviewFirst =
    step === "questions" &&
    needsReviewStep({
      form: "incident",
      answeredFollowups: questions.filter((question) => (answers[question.id] ?? "").trim() !== "")
        .length,
    });

  /**
   * Check what the person entered; when something is missing or wrong, mark
   * it, take them to the first one and say how many. Returns whether to hold.
   */
  const holdSubmit = (): boolean => {
    const nextSubjectError = validateOptionalSubject(subject);
    const nextDetailsError = validateIncidentDetails(details);
    setSubjectError(nextSubjectError);
    setDetailsError(nextDetailsError);
    setForProblem(forPending);
    const needed = nextDetailsError === "detailsRequired" ? 1 : 0;
    const toFix =
      (nextSubjectError === null ? 0 : 1) +
      (nextDetailsError === null || nextDetailsError === "detailsRequired" ? 0 : 1) +
      (forPending ? 1 : 0);
    if (needed + toFix === 0) {
      return false;
    }
    revealFirstProblemSoon(() => formRef.current);
    announce(needed, toFix);
    return true;
  };

  const backToForm = (fieldKey?: string) => {
    setReviewing(false);
    if (fieldKey !== undefined) {
      revealFieldSoon(() => formRef.current, fieldKey);
    }
  };
  const paste = usePasteToAttach({ disabled: busy });
  // Pastes inside the "Anything else?" box land in the uploader right under it.
  const laterPaste = usePasteToAttach({ disabled: busy });
  /** What triage looks at: the chat's files and those added with the description. */
  const describedAttachmentIds = () =>
    Array.from(new Set([...extraIdsRef.current, ...addedIdsRef.current]));
  /** Everything the ticket carries, "Anything else?" attachments included. */
  const currentAttachmentIds = () =>
    Array.from(new Set([...describedAttachmentIds(), ...laterIdsRef.current]));
  const trackPending = (which: "first" | "later", pending: boolean) => {
    pendingRef.current = { ...pendingRef.current, [which]: pending };
    const any = pendingRef.current.first || pendingRef.current.later;
    uploadingRef.current = any;
    setUploading(any);
  };

  const runTriage = async (input: IncidentTriageInput): Promise<TriageOutcome> => {
    const key = incidentTriageKey(input);
    const cached = triageCache.current.get(key);
    if (cached !== undefined) {
      logger.debug("[tickets] incident triage from cache");
      return cached;
    }
    let outcome: TriageOutcome = { found: NOTHING_DERIVED, asked: [] };
    try {
      const result = await followups.mutateAsync({
        subject: input.subject,
        details: input.details,
        body_lang: language,
        attachment_ids: [...input.attachmentIds],
      });
      outcome = {
        found: {
          subject: result.suggested_subject,
          category: result.category ?? null,
          // Both or neither; the server re-validates the pair on create.
          service: result.service && result.service_category ? result.service : null,
          serviceCategory:
            result.service && result.service_category ? result.service_category : null,
        },
        asked: result.questions ?? [],
      };
      logger.debug(
        "[tickets] incident follow-ups",
        outcome.asked.length,
        outcome.found.category,
        outcome.found.service === null ? "unclassified" : "classified",
      );
    } catch (error) {
      // Never block on the follow-ups: carry on with what they wrote. The
      // miss is cached too, so a retry files the same body rather than
      // turning into a different ticket.
      logger.warn("[tickets] follow-up questions unavailable; filing directly", error);
    }
    triageCache.current.set(key, outcome);
    return outcome;
  };

  const file = async (
    asked: readonly IncidentFollowupQuestion[],
    given: Record<string, string>,
    more: string,
    found: Derived,
  ) => {
    if (filing.current) {
      return;
    }
    const ownSubject = ownIncidentSubject(subject);
    const attachmentIds = currentAttachmentIds();
    const moreDetails = more.trim();
    const draft: IncidentCreateRequest = {
      kind: "INCIDENT",
      subject: ownSubject ?? found.subject,
      description: details.trim(),
      body_lang: language,
      urgency_code: urgency,
      system: found.category,
      followups: asked.map((question) => ({
        question: question.text,
        answer: (given[question.id] ?? "").trim(),
      })),
      // Unanswered is omitted, not sent blank.
      ...(moreDetails === "" ? {} : { additional_details: moreDetails }),
      // Ivanti has both on the signed-in person's employee record.
      location: null,
      phone: null,
      attachment_ids: attachmentIds,
      conversation_id: conversationId,
      // Omitted for the signed-in person, so their body is what it always was.
      ...onBehalfField(forPerson, signedInEmail),
    };
    const fingerprint = incidentAttemptFingerprint(draft, ownSubject);
    let used = found;
    if (filedWith.current?.fingerprint === fingerprint) {
      // A retry of the same attempt: send the byte-identical body, so the
      // server replays the first ticket instead of refusing the key.
      used = filedWith.current.found;
    } else {
      filedWith.current = { fingerprint, found };
    }
    const body: IncidentCreateRequest = {
      ...draft,
      subject: ownSubject ?? used.subject,
      system: used.category,
      service_id: used.service,
      category_id: used.serviceCategory,
    };
    const someoneElse = isSomeoneElse(forPerson, signedInEmail);
    if (
      shouldCheckSimilar({
        forSomeoneElse: someoneElse,
        kiosk: kiosk !== null,
        checkedFingerprint: similarChecked.current,
        fingerprint,
      })
    ) {
      similarChecked.current = fingerprint;
      const text = {
        subject: body.subject ?? null,
        description: [body.description, body.additional_details ?? ""]
          .filter((part) => part.trim() !== "")
          .join("\n\n"),
        system: body.system ?? null,
      };
      let matches: SimilarTicket[] = [];
      filing.current = true;
      setChecking(true);
      try {
        matches = (await findSimilar.mutateAsync(text)).items ?? [];
      } catch (error) {
        // Never in the way of filing: no answer is "nothing similar".
        logger.warn("[tickets] similar-ticket check unavailable; filing directly", error);
      } finally {
        filing.current = false;
        setChecking(false);
      }
      if (matches.length > 0) {
        logger.debug("[tickets] open ticket(s) about the same thing", matches.length);
        setJoinFailed(false);
        setSimilar({ matches, body, fingerprint, text });
        return;
      }
    }
    await submitBody(body, fingerprint, someoneElse ? forPerson : null);
  };

  const submitBody = async (
    body: IncidentCreateRequest,
    fingerprint: string,
    forColleague: OnBehalfPerson | null,
  ) => {
    const toFile = withNotSameAs(body, fingerprint, notSame.current);
    logger.debug("[tickets] filing incident", {
      followups: toFile.followups?.filter((item) => item.answer !== "").length ?? 0,
      additionalDetails: (toFile.additional_details ?? "") !== "",
      attachments: toFile.attachment_ids?.length ?? 0,
      forColleague: toFile.on_behalf_of_email != null,
      notSameAs: toFile.not_same_as?.length ?? 0,
    });
    filing.current = true;
    try {
      await onSubmit(toFile, fingerprint, forColleague);
    } catch (error) {
      logger.warn("[tickets] incident filing failed", error);
    } finally {
      filing.current = false;
    }
  };

  /** "Yes, add me to it": noted on their ticket; nothing of ours is filed. */
  const handleJoin = (match: SimilarTicket) => {
    if (similar === null || joiningId !== null || filing.current) {
      return;
    }
    setJoiningId(match.ticket_id);
    setJoinFailed(false);
    void (async () => {
      try {
        const joined = await joinSimilar.mutateAsync({
          ticket_id: match.ticket_id,
          ...similar.text,
        });
        logger.debug("[tickets] added to an open ticket", joined.ticket_number);
        onJoined({ number: joined.ticket_number, subject: joined.subject });
      } catch (error) {
        logger.warn("[tickets] could not add the person to the open ticket", error);
        setJoinFailed(true);
      } finally {
        setJoiningId(null);
      }
    })();
  };

  /** "No, mine is different": file it, telling IT which ticket it is not. */
  const handleDifferent = () => {
    if (similar === null || joiningId !== null || filing.current) {
      return;
    }
    const pending = similar;
    notSame.current = {
      fingerprint: pending.fingerprint,
      numbers: notSameNumbers(pending.matches),
    };
    setFilingOwn(true);
    void submitBody(pending.body, pending.fingerprint, null).finally(() => {
      setFilingOwn(false);
      setSimilar(null);
    });
  };

  /* A chat card that opened on review still wants a category: ask triage for
     it once, in the background. Its questions are ignored — the chat already
     asked — and nothing is filed until the person presses Submit. */
  const backgroundTriage = useRef(false);
  useEffect(() => {
    if (start.step !== "questions" || backgroundTriage.current) {
      return;
    }
    backgroundTriage.current = true;
    void runTriage({
      subject: ownIncidentSubject(subject),
      details: details.trim(),
      attachmentIds: describedAttachmentIds(),
    }).then((outcome) => setTriage(outcome.found));
    // Once, on mount, with the prefill as it arrived.
  }, []);

  const handleContinue = () => {
    if (continuing.current || filing.current || busy || uploadingRef.current) {
      return;
    }
    if (holdSubmit()) {
      return;
    }

    const input: IncidentTriageInput = {
      subject: ownIncidentSubject(subject),
      details: details.trim(),
      attachmentIds: describedAttachmentIds(),
    };

    if (fromChat || chatQuestions.length > 0) {
      // The assistant already asked: back to review with its questions (and
      // the answers it heard, or the person's edits of them), if any. Triage
      // still runs, in the background and only for the category.
      setQuestions(chatQuestions);
      setStep("questions");
      void runTriage(input).then((outcome) => setTriage(outcome.found));
      return;
    }

    continuing.current = true;
    setChecking(true);
    void (async () => {
      try {
        const outcome = await runTriage(input);
        setTriage(outcome.found);
        const attachmentsMoved =
          uploadingRef.current ||
          incidentTriageKey({ ...input, attachmentIds: describedAttachmentIds() }) !==
            incidentTriageKey(input);
        if (outcome.asked.length === 0 && !attachmentsMoved) {
          setChecking(false);
          await file([], {}, "", outcome.found);
          return;
        }
        // Questions to ask — or none, but a screenshot is still on its way,
        // so stop at "Ready to submit" rather than filing without it.
        if (!sameQuestions(outcome.asked, questions)) {
          setAnswers({});
        }
        setQuestions(outcome.asked);
        setStep("questions");
      } finally {
        continuing.current = false;
        setChecking(false);
      }
    })();
  };

  const handleSubmit = () => {
    if (busy || uploading || filing.current || continuing.current) {
      return;
    }
    if (step === "describe") {
      handleContinue();
      return;
    }
    if (holdSubmit()) {
      return;
    }
    if (reviewFirst && !reviewing) {
      setReviewing(true);
      return;
    }
    // "Anything else?" is sent only when it was on screen.
    fileFromReview(questions, answers, anythingElse ? additional : "");
  };

  const handleSkip = () => {
    if (busy || uploading || filing.current || continuing.current) {
      return;
    }
    // Skipping drops the answers, not the attachments: those are still sent.
    fileFromReview([], {}, "");
  };

  /**
   * File from the review step. The subject and description stay editable
   * there, so they are validated again, and the derived subject and category
   * come from triage on the text as it now stands. Unchanged text is a cache
   * hit (`runTriage`), so this only waits when the person rewrote it; the
   * questions already on screen are kept either way.
   */
  const fileFromReview = (
    asked: readonly IncidentFollowupQuestion[],
    given: Record<string, string>,
    more: string,
  ) => {
    if (holdSubmit()) {
      return;
    }
    continuing.current = true;
    setChecking(true);
    void (async () => {
      let found = triage;
      try {
        const outcome = await runTriage({
          subject: ownIncidentSubject(subject),
          details: details.trim(),
          attachmentIds: describedAttachmentIds(),
        });
        found = outcome.found;
        setTriage(found);
      } finally {
        continuing.current = false;
        setChecking(false);
      }
      await file(asked, given, more, found);
    })();
  };

  const onForPending = (pending: boolean) => {
    setForPending(pending);
    if (!pending) {
      setForProblem(false);
    }
  };

  /* Checked on the press too, before the Form's own validation can swallow
     it without a word (7 Oct: never block silently). */
  const guardPress = (event: { preventDefault: () => void }) => {
    if (!busy && !uploading && holdSubmit()) {
      event.preventDefault();
    }
  };

  /** "Here's what IT will get": what was entered, answered questions only. */
  const reviewRows = (): FormReviewRow[] => {
    const rows: FormReviewRow[] = [];
    if (forPerson !== null) {
      rows.push({
        key: "for",
        label: t("onBehalf.label"),
        value: forPerson.displayName,
        onEdit: () => backToForm("for"),
      });
    }
    if (subject.trim() !== "") {
      rows.push({
        key: "subject",
        label: t("incident.subject.label"),
        value: subject.trim(),
        onEdit: () => backToForm("subject"),
      });
    }
    rows.push(
      {
        key: "details",
        label: t("incident.details.label"),
        value: details.trim(),
        onEdit: () => backToForm("details"),
      },
      {
        key: "urgency",
        label: t("incident.impact.label"),
        value: urgency === "UNKNOWN" ? "" : t(`incident.impact.${urgency}`),
        onEdit: () => backToForm("urgency"),
      },
    );
    for (const question of questions) {
      const answer = (answers[question.id] ?? "").trim();
      if (answer !== "") {
        rows.push({
          key: `followup-${question.id}`,
          label: question.text,
          value: answer,
          onEdit: () => backToForm(`followup-${question.id}`),
        });
      }
    }
    if (anythingElse && additional.trim() !== "") {
      rows.push({
        key: "additional",
        label: t("incident.more.label"),
        value: additional.trim(),
        onEdit: () => backToForm("additional"),
      });
    }
    const files = fileCounts.first + fileCounts.later + extraAttachmentIds.length;
    if (files > 0) {
      rows.push({
        key: "files",
        label: t("incident.attachLabel"),
        value: tCommon("formCheck.review.files", { count: files }),
      });
    }
    return rows;
  };

  return (
    <Form
      ref={formRef}
      // The summary hides the form rather than unmounting it: unmounting
      // would drop the uploads. Only `data-review-keep` children stay.
      className={cn("flex flex-col gap-6", (reviewing || similar !== null) && REVIEW_HIDES_FORM)}
      onFormSubmit={handleSubmit}
      onPaste={paste.onPaste}
    >
      {submitError === null || submitError === undefined ? null : (
        <div data-form-problem data-review-keep>
          <ErrorState variant="inline" error={submitError} offerHelpdeskContact />
        </div>
      )}

      {similar === null ? null : (
        <SimilarTicketPrompt
          matches={similar.matches}
          joiningId={joiningId}
          joinFailed={joinFailed}
          filing={filingOwn || submitting}
          onJoin={handleJoin}
          onDifferent={handleDifferent}
        />
      )}

      {reviewing && similar === null ? (
        <FormReview
          rows={reviewRows()}
          onBack={() => backToForm()}
          onSend={handleSubmit}
          sending={busy || uploading}
          sendLabel={t("incident.followups.submit")}
          footer={<FilingAsLine className="justify-end text-end" />}
        />
      ) : null}

      <FieldHighlightLegend />

      {kiosk === null ? (
        <div data-field-key="for">
          {prefilledFor === null ? (
            <OnBehalfField
              person={forPerson}
              onChange={setForPerson}
              signedInEmail={signedInEmail}
              disabled={busy}
              onTextPendingChange={onForPending}
              pickError={forProblem ? checkMessage("notPicked") : undefined}
            />
          ) : (
            <HighlightedField state={forPersonState}>
              <OnBehalfField
                person={forPerson}
                onChange={setForPerson}
                signedInEmail={signedInEmail}
                disabled={busy}
                onTextPendingChange={onForPending}
                pickError={forProblem ? checkMessage("notPicked") : undefined}
              />
            </HighlightedField>
          )}
        </div>
      ) : null}

      <HighlightedField state={subjectState} fieldKey="subject">
        <Field
          name="subject"
          label={t("incident.subject.label")}
          hint={t("incident.subject.hint")}
          error={message(subjectError)}
          disabled={busy}
        >
          <Input
            name="subject"
            value={subject}
            onChange={(event) => {
              setSubject(event.target.value);
              if (subjectError !== null && validateOptionalSubject(event.target.value) === null) {
                setSubjectError(null);
              }
            }}
            placeholder={t("create.subjectPlaceholder")}
            maxLength={SUBJECT_MAX_LENGTH}
          />
        </Field>
      </HighlightedField>

      <HighlightedField state={detailsState} fieldKey="details">
        <Field
          name="details"
          label={t("incident.details.label")}
          error={message(detailsError)}
          required
          disabled={busy}
        >
          <Textarea
            name="details"
            value={details}
            onChange={(event) => {
              setDetails(event.target.value);
              if (detailsError !== null && validateIncidentDetails(event.target.value) === null) {
                setDetailsError(null);
              }
            }}
            placeholder={t("incident.details.placeholder")}
            rows={6}
            autoResize
            maxLength={DESCRIPTION_MAX_LENGTH}
          />
        </Field>
      </HighlightedField>

      <Field
        name={attachmentsId}
        label={t("incident.attachLabel")}
        hint={t("incident.attachHint")}
        disabled={busy}
      >
        <AttachmentUploader
          ref={paste.ref}
          id={attachmentsId}
          onChange={(ids) => {
            addedIdsRef.current = ids;
            setFileCounts((previous) => ({ ...previous, first: ids.length }));
          }}
          onPendingChange={(pending) => trackPending("first", pending)}
          disabled={busy}
        />
      </Field>

      {/* Everything above stays editable on the review step too: a prefill or a
          first draft the person wants to correct is fixed in place, with no
          extra "Edit" step (Jay and Rusty, 30 Sep). Submit re-checks it. */}
      <HighlightedField state={urgencyState} fieldKey="urgency">
        <Field name="urgency_code" label={t("incident.impact.label")} disabled={busy}>
          <RadioGroup
            value={urgency}
            onValueChange={(value) => setUrgency(value as TicketLevelCode)}
            className="flex flex-col gap-2"
          >
            {IMPACT_CHOICES.map((level) => (
              <Radio.Root key={level} value={level} label={t(`incident.impact.${level}`)} />
            ))}
          </RadioGroup>
        </Field>
      </HighlightedField>

      {step === "questions" ? (
        <>
          <Separator />
          <div className="flex flex-col gap-1">
            <Typography as="h3" variant="label">
              {questions.length === 0
                ? t("incident.followups.readyTitle")
                : t("incident.followups.title")}
            </Typography>
            <Typography variant="body-sm" tone="muted">
              {questions.length === 0
                ? t("incident.followups.readyIntro")
                : fromChat
                  ? t("incident.followups.chatIntro")
                  : t("incident.followups.intro")}
            </Typography>
          </div>
          {questions.map((question) => (
            // Blue while it still holds the answer the chat heard (7 Oct).
            <HighlightedField
              key={question.id}
              fieldKey={`followup-${question.id}`}
              state={prefillHighlight(start.answers[question.id] ?? null, answers[question.id])}
            >
              <Field
                name={`followup-${question.id}`}
                label={question.text}
                // `busy`, not `submitting`: the answers are read when Continue is
                // pressed, so an edit during the triage check would be lost.
                disabled={busy}
              >
                <Textarea
                  name={`followup-${question.id}`}
                  value={answers[question.id] ?? ""}
                  onChange={(event) =>
                    setAnswers((previous) => ({ ...previous, [question.id]: event.target.value }))
                  }
                  placeholder={t("incident.followups.answerPlaceholder")}
                  rows={2}
                  autoResize
                  maxLength={FOLLOWUP_ANSWER_MAX_LENGTH}
                />
              </Field>
            </HighlightedField>
          ))}
        </>
      ) : null}

      {/* Always mounted, shown only on review (see `showsAnythingElse`): unmounting would
          drop its uploads without telling the id list. */}
      <div
        className={cn("flex flex-col gap-3", anythingElse ? null : "hidden")}
        onPaste={laterPaste.onPaste}
        data-field-key="additional"
      >
        <Field name="additional_details" label={t("incident.more.label")} disabled={busy}>
          <Textarea
            name="additional_details"
            value={additional}
            onChange={(event) => setAdditional(event.target.value)}
            placeholder={t("incident.more.placeholder")}
            rows={6}
            autoResize
            maxLength={ADDITIONAL_DETAILS_MAX_LENGTH}
          />
        </Field>
        <AttachmentUploader
          ref={laterPaste.ref}
          id={moreAttachmentsId}
          onChange={(ids) => {
            laterIdsRef.current = ids;
            setFileCounts((previous) => ({ ...previous, later: ids.length }));
          }}
          onPendingChange={(pending) => trackPending("later", pending)}
          disabled={busy}
        />
      </div>

      {/* Shared PCs: whose name this goes out under, where Submit is pressed. */}
      <FilingAsLine className="-mb-3 justify-end text-end" />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Typography variant="caption" tone="muted">
          {t("incident.notBroken.prompt")}{" "}
          <Link
            to="/tickets/new?kind=SERVICE_REQUEST"
            className="text-accent-fg-subtle font-medium underline-offset-2 hover:underline"
          >
            {t("incident.notBroken.action")}
          </Link>
        </Typography>

        <div className="flex flex-wrap items-center justify-end gap-2">
          {onCancel === undefined || step === "questions" ? null : (
            <Button type="button" variant="ghost" tone="neutral" onClick={onCancel}>
              {tCommon("button.cancel")}
            </Button>
          )}
          {step === "questions" ? (
            <>
              {!offersSkip({ questionCount: questions.length, fromChat }) ? null : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSkip}
                  disabled={busy || uploading}
                >
                  {t("incident.followups.skip")}
                </Button>
              )}
              <Button
                type="submit"
                loading={busy || uploading}
                disabled={busy || uploading}
                onClick={guardPress}
              >
                {submitLabel(
                  reviewFirst ? tCommon("formCheck.review.next") : t("incident.followups.submit"),
                  stillNeeded,
                )}
              </Button>
            </>
          ) : (
            <Button
              type="submit"
              loading={busy || uploading}
              disabled={busy || uploading}
              onClick={guardPress}
            >
              {uploading
                ? t("attachment.uploading")
                : checking
                  ? t("incident.checking")
                  : submitting
                    ? t("incident.submitting")
                    : submitLabel(t("incident.continue"), stillNeeded)}
            </Button>
          )}
        </div>
      </div>
      <FormAnnouncer message={announcement} />
    </Form>
  );
}
